import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import 'moment/locale/ru';
import {
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  CircularProgress,
} from '@material-ui/core';
import api from '../../lib/api';
import AdminPageShell from '../../components/admin/AdminPageShell';
import MaterialIcon from '../../components/admin/MaterialIcon';
import {
  getNotificationTypeLabel,
  resolveAdminNotificationTarget,
} from '../../lib/notificationNav';
import { withRouter } from 'react-router-dom';
import {
  MdOutlineNotifications,
  MdOutlineNotificationsOff,
} from 'react-icons/md';

moment.locale('ru');

class Notifications extends Component {
  constructor(props) {
    super(props);
    this.state = {
      notifications: {
        new: [],
        read: [],
      },
      loading: true,
      markingAsRead: false,
    };
  }

  componentDidMount() {
    this.loadNotifications();
  }

  loadNotifications = async () => {
    try {
      this.setState({ loading: true });
      const data = await api.getUserNotifications();
      console.log('Загружены уведомления:', data);
      // Логируем первое уведомление для отладки
      if (data.new && data.new.length > 0) {
        console.log('Пример нового уведомления:', data.new[0]);
      }
      if (data.read && data.read.length > 0) {
        console.log('Пример прочитанного уведомления:', data.read[0]);
      }
      this.setState({
        notifications: {
          new: data.new || [],
          read: data.read || [],
        },
        loading: false,
      });
    } catch (error) {
      console.error('Ошибка загрузки уведомлений:', error);
      this.setState({ loading: false });
    }
  };

  handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await api.markNotificationAsRead(notification.id);
        await this.loadNotifications();
      } catch (error) {
        console.error('Ошибка при отметке уведомления как прочитанного:', error);
      }
    }

    const target = resolveAdminNotificationTarget(notification);
    if (!target) return;

    if (target.kind === 'href') {
      if (this.props.history) {
        this.props.history.push(target.href);
      }
      return;
    }

    if (target.kind === 'booking') {
      try {
        const booking = await api.getBooking(target.objectId);
        if (booking && booking.playgroundId && booking.date) {
          const bookingDate = moment(booking.date).format('YYYY-MM-DD');
          const url = `/playground/${booking.playgroundId}?date=${bookingDate}`;
          if (this.props.history) {
            this.props.history.push(url);
          }
        }
      } catch (error) {
        console.error('Ошибка при получении информации о бронировании:', error);
      }
    }
  };

  handleMarkAllAsRead = async () => {
    try {
      this.setState({ markingAsRead: true });
      await api.markAllNotificationsAsRead();
      await this.loadNotifications();
    } catch (error) {
      console.error('Ошибка при отметке всех уведомлений:', error);
    } finally {
      this.setState({ markingAsRead: false });
    }
  };

  formatDate = (date) => {
    return moment(date).format('DD.MM.YYYY HH:mm');
  };

  render() {
    const { classes } = this.props;
    const { notifications, loading, markingAsRead } = this.state;
    const { new: newNotifications, read: readNotifications } = notifications;
    const hasNewNotifications = newNotifications.length > 0;

    const markAllAction =
      hasNewNotifications ? (
        <Button
          variant="contained"
          color="primary"
          onClick={this.handleMarkAllAsRead}
          disabled={markingAsRead}
          className={classes.markAllButton}
        >
          {markingAsRead ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Обработка...
            </>
          ) : (
            'Отметить все как прочитанные'
          )}
        </Button>
      ) : null;

    if (loading) {
      return (
        <AdminPageShell title="Уведомления">
          <Paper className={classes.paper} elevation={2}>
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body1" style={{ marginTop: 16 }}>
                Загрузка уведомлений...
              </Typography>
            </div>
          </Paper>
        </AdminPageShell>
      );
    }

    return (
      <AdminPageShell title="Уведомления" actions={markAllAction}>
        <Paper className={classes.paper} elevation={2}>
          {hasNewNotifications && (
            <>
              <div className={classes.sectionHeader}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Новые
                  <Chip
                    label={newNotifications.length}
                    color="primary"
                    size="small"
                    style={{ marginLeft: 8 }}
                  />
                </Typography>
              </div>
              <List>
                {newNotifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      button
                      onClick={() => this.handleNotificationClick(notification)}
                      className={classes.notificationItem}
                    >
                      <ListItemIcon className={classes.unreadIconWrap}>
                        <MaterialIcon icon={MdOutlineNotifications} className={classes.unreadIcon} size={22} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <div className={classes.notificationHeader}>
                            <Typography variant="subtitle1" className={classes.notificationTitle}>
                              {notification.title}
                            </Typography>
                            <Chip
                              label={getNotificationTypeLabel(notification.type || notification.Type)}
                              size="small"
                              className={classes.typeChip}
                            />
                          </div>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" component="span" className={classes.notificationBody}>
                              {notification.body}
                            </Typography>
                            <br />
                            <Typography variant="caption" component="span" className={classes.notificationDate}>
                              {this.formatDate(notification.sendDate)}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < newNotifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}

          {readNotifications.length > 0 && (
            <>
              {hasNewNotifications && <Divider className={classes.sectionDivider} />}
              <div className={classes.sectionHeader}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Прочитанные
                </Typography>
              </div>
              <List>
                {readNotifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <ListItem
                      button
                      onClick={() => this.handleNotificationClick(notification)}
                      className={classes.notificationItemRead}
                    >
                      <ListItemIcon>
                        <MaterialIcon icon={MdOutlineNotificationsOff} className={classes.readIcon} size={22} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <div className={classes.notificationHeader}>
                            <Typography variant="subtitle1" className={classes.notificationTitleRead}>
                              {notification.title}
                            </Typography>
                            <Chip
                              label={getNotificationTypeLabel(notification.type || notification.Type)}
                              size="small"
                              variant="outlined"
                              className={classes.typeChip}
                            />
                          </div>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" component="span" className={classes.notificationBodyRead}>
                              {notification.body}
                            </Typography>
                            <br />
                            <Typography variant="caption" component="span" className={classes.notificationDate}>
                              {this.formatDate(notification.sendDate)}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < readNotifications.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}

          {!hasNewNotifications && readNotifications.length === 0 && (
            <div className={classes.emptyContainer}>
              <MaterialIcon icon={MdOutlineNotifications} className={classes.emptyIcon} size={48} />
              <Typography variant="h6" className={classes.emptyTitle}>
                Нет уведомлений
              </Typography>
              <Typography variant="body2" className={classes.emptyText}>
                Здесь будут отображаться ваши уведомления о бронированиях, встречах и других событиях
              </Typography>
            </div>
          )}
        </Paper>
      </AdminPageShell>
    );
  }
}

const styles = (theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },
  markAllButton: {
    textTransform: 'none',
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  sectionHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  sectionDivider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  notificationItem: {
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  unreadIconWrap: {
    minWidth: 40,
  },
  unreadIcon: {
    color: theme.palette.primary.main,
  },
  readIcon: {
    color: theme.palette.text.disabled,
  },
  notificationItemRead: {
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    opacity: 0.7,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      opacity: 1,
    },
  },
  notificationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  notificationTitle: {
    fontWeight: 600,
    flex: 1,
  },
  notificationTitleRead: {
    fontWeight: 500,
    flex: 1,
  },
  notificationBody: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  notificationBodyRead: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  notificationDate: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.disabled,
  },
  typeChip: {
    marginLeft: theme.spacing(1),
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
    textAlign: 'center',
  },
  emptyIcon: {
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
  emptyTitle: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  emptyText: {
    color: theme.palette.text.disabled,
    maxWidth: 400,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
});

const Styled = withRouter(withStyles(styles)(Notifications));
export default Styled;

